import { useMemo, useState } from 'react';
import { Card, Icon, IconButton } from '../ds';
import { EmptyState, Screen, ScreenTitle } from '../components/primitives';
import { AddPhotoTile, PhotoLightbox, PhotoTile } from '../components/PhotoTile';
import { useToast } from '../components/Toast';
import * as api from '../lib/db';
import { useFamily } from '../state/FamilyContext';
import { addDays, formatDateShortJa, startOfDay } from '../lib/time';
import type { Member, Photo } from '../lib/types';

/**
 * アルバム / Album.
 *
 * A shared grid, explicitly marked as visible to the grandparents. Photos are
 * grouped by week and the newest week leads with a hero tile, which keeps the
 * mosaic rhythm of the design while the content is real and unbounded.
 *
 * `readOnly` is what a grandparent sees: the same album, no upload control, no
 * delete, no share toggle.
 */

export function AlbumScreen({
  readOnly = false,
  onOpenShare,
}: {
  readOnly?: boolean;
  onOpenShare?: () => void;
}) {
  const { familyId, family, me, photos } = useFamily();
  const toast = useToast();

  const [viewing, setViewing] = useState<Photo | null>(null);
  const [uploading, setUploading] = useState(false);

  const groups = useMemo(() => groupByWeek(photos), [photos]);

  const upload = async (files: File[]) => {
    if (!familyId || !me) return;
    setUploading(true);
    try {
      // Sequential rather than parallel: a phone on a train uploading six
      // photos at once tends to fail all six.
      for (const file of files) {
        await api.addPhoto(familyId, me, file);
      }
      toast.show({ title: '写真を追加しました', message: `${files.length}枚` });
    } catch (e) {
      toast.show({
        title: 'アップロードできませんでした',
        message: e instanceof Error ? e.message : undefined,
        tone: 'danger',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Screen>
      <ScreenTitle eyebrow="Album · おもいで" title="アルバム" />

      {/* ---- sharing banner ---- */}
      <Card
        padding="sm"
        style={{ marginBottom: 16, background: 'var(--accent-subtle)', borderColor: 'transparent' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span
            style={{
              flex: 'none',
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: family?.shareEnabled ? 'var(--sky-500)' : 'var(--sand-400)',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name={family?.shareEnabled ? 'Eye' : 'EyeOff'} size={17} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--text)' }}>
              {readOnly
                ? '共有アルバム'
                : family?.shareEnabled
                  ? 'おじいちゃん・おばあちゃんと共有中'
                  : 'まだ共有していません'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {readOnly
                ? 'Shared with you · view only'
                : family?.shareEnabled
                  ? 'Shared with grandparents · view only'
                  : 'Not shared yet · tap to share'}
            </div>
          </div>
          {!readOnly && onOpenShare ? (
            <IconButton icon="Share2" label="共有の設定" variant="soft" size="sm" onClick={onOpenShare} />
          ) : null}
        </div>
      </Card>

      {/* ---- grid ---- */}
      {!readOnly ? (
        <div style={{ marginBottom: 18 }}>
          <AddPhotoTile height={96} placeholder="写真を追加 · Add photos" onFiles={upload} busy={uploading} />
        </div>
      ) : null}

      {groups.length ? (
        groups.map((group, groupIndex) => (
          <div key={group.key} style={{ marginBottom: 18 }}>
            <div
              className="eyebrow"
              style={{ marginBottom: 9, fontSize: 'var(--text-xs)' }}
            >
              {group.label}
            </div>

            {/* The newest group leads with a hero, matching the design's mosaic. */}
            {groupIndex === 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <PhotoTile photo={group.photos[0]} height={180} onOpen={setViewing} />
                </div>
                {group.photos.slice(1).map((photo) => (
                  <PhotoTile key={photo.id} photo={photo} height={130} onOpen={setViewing} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 9 }}>
                {group.photos.map((photo) => (
                  <PhotoTile key={photo.id} photo={photo} height={108} radius={16} onOpen={setViewing} />
                ))}
              </div>
            )}
          </div>
        ))
      ) : (
        <Card padding="md">
          <EmptyState icon={<Icon name="Image" size={24} />}>
            {readOnly
              ? 'まだ写真がありません。'
              : 'まだ写真がありません。はじめの一枚を追加しましょう。'}
          </EmptyState>
        </Card>
      )}

      {viewing ? (
        <PhotoLightbox
          photo={viewing}
          onClose={() => setViewing(null)}
          onDelete={
            readOnly || !familyId
              ? undefined
              : async (photo) => {
                  await api.deletePhoto(familyId, photo);
                  toast.show({ title: '削除しました' });
                }
          }
        />
      ) : null}
    </Screen>
  );
}

type PhotoGroup = { key: string; label: string; photos: Photo[] };

/**
 * Group into 今週 / 先週 / それ以前, then by month once the album has history.
 * Photos arrive newest-first from Firestore, so groups keep that order.
 */
function groupByWeek(photos: Photo[]): PhotoGroup[] {
  if (!photos.length) return [];

  const now = new Date();
  const thisWeekStart = startOfDay(addDays(now, -now.getDay()));
  const lastWeekStart = addDays(thisWeekStart, -7);

  const thisWeek: Photo[] = [];
  const lastWeek: Photo[] = [];
  const older = new Map<string, Photo[]>();

  for (const photo of photos) {
    if (photo.takenAt >= thisWeekStart) thisWeek.push(photo);
    else if (photo.takenAt >= lastWeekStart) lastWeek.push(photo);
    else {
      const key = `${photo.takenAt.getFullYear()}-${photo.takenAt.getMonth() + 1}`;
      const bucket = older.get(key);
      if (bucket) bucket.push(photo);
      else older.set(key, [photo]);
    }
  }

  const groups: PhotoGroup[] = [];
  if (thisWeek.length) groups.push({ key: 'this-week', label: '今週 · This week', photos: thisWeek });
  if (lastWeek.length) groups.push({ key: 'last-week', label: '先週 · Last week', photos: lastWeek });

  for (const [key, bucket] of older) {
    const [year, month] = key.split('-').map(Number);
    groups.push({
      key,
      label: `${year}年${month}月 · ${new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
      photos: bucket,
    });
  }

  return groups;
}

/** Caption line used by the lightbox and the read-only view. */
export function photoCaption(photo: Photo, author?: Member): string {
  return photo.caption ?? `${formatDateShortJa(photo.takenAt)}${author ? ` · ${author.name}` : ''}`;
}
